/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Award, Users, Target, Globe, Mail } from "lucide-react";
import { teamMembers } from "@/data";
import { JSX } from "react/jsx-runtime";

const LinkedInIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

export const Route = createFileRoute("/about")({
  component: AboutPage,
});

const values = [
  {
    icon: Award,
    title: "Excellence",
    description:
      "We hold ourselves to the highest professional standards in everything we do.",
  },
  {
    icon: Users,
    title: "Client-First",
    description:
      "Your success is our priority. We listen, understand, and deliver tailored solutions.",
  },
  {
    icon: Target,
    title: "Accuracy",
    description:
      "Tax is unforgiving. We maintain zero-tolerance for errors in our work.",
  },
  {
    icon: Globe,
    title: "Integrity",
    description:
      "Absolute confidentiality and ethical conduct in every client relationship.",
  },
];

function AboutPage(): JSX.Element {
  return (
    <>
      <Helmet>
        <title>About Us | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Learn about Rosewood Tax Consultancy — Kenya's trusted tax professionals with over 15 years of experience in tax compliance, advisory, and dispute resolution."
        />
      </Helmet>

      <section className="bg-[#FAF7F2] pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >

            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-6 leading-tight">
              Kenya's Trusted
              <br />
              <span className=" text-[#6B1E2A]">Tax Partner</span>
            </h1>
            <p className="text-xl text-[#6B6B6B] leading-relaxed">
              Founded on the principle that every business deserves world-class
              tax expertise, Rosewood Tax Consultancy has been Kenya's go-to
              firm for comprehensive, reliable, and strategic tax services since
              2008.
            </p>
          </motion.div>
        </div>
      </section>


      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-4xl font-semibold text-[#1A1A1A] mb-6">
                Our Story
              </h2>
              <div className="space-y-5 text-[#6B6B6B] leading-relaxed">
                <p>
                  Rosewood Tax Consultancy was established in Nairobi in 2008 by
                  Elizabeth Rosewood, a seasoned tax professional with a vision:
                  to create a consultancy that genuinely served the interests of
                  Kenyan businesses rather than simply processing compliance
                  paperwork.
                </p>
                <p>
                  Over fifteen years, we have grown from a boutique firm of
                  three to a team of over 20 qualified professionals. Today, we
                  serve hundreds of clients across Kenya — from startups to
                  multinationals, from individual entrepreneurs to NGOs.
                </p>
                <p>
                  Our philosophy has never changed: deep expertise, proactive
                  advice, and absolute commitment to client success. Every tax
                  challenge we face, we face together with our clients.
                </p>
              </div>
            </motion.div>

  
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { value: "2008", label: "Founded in Nairobi" },
                { value: "20+", label: "Tax Professionals" },
                { value: "500+", label: "Active Clients" },
                { value: "KES 2B+", label: "Tax Savings Achieved" },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  className={`p-7 rounded-xl ${
                    i % 2 === 0
                      ? "bg-[#6B1E2A] text-white"
                      : "bg-[#FAF7F2]"
                  }`}
                >
                  <p
                    className={`font-serif text-3xl font-bold mb-1 ${i % 2 === 0 ? "text-[#C9A84C]" : "text-[#6B1E2A]"}`}
                  >
                    {stat.value}
                  </p>
                  <p
                    className={`text-sm ${i % 2 === 0 ? "text-white/70" : "text-[#6B6B6B]"}`}
                  >
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="font-serif text-4xl font-semibold text-[#1A1A1A] mb-4">
              Our Values
            </h2>
            <p className="text-[#6B6B6B] max-w-lg mx-auto">
              These four principles guide every decision we make and every
              service we deliver.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-7 rounded-xl text-center"
                >
                  <div className="w-14 h-14 bg-[#6B1E2A]/8 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon size={24} className="text-[#6B1E2A]" />
                  </div>
                  <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-[#6B6B6B] leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="font-serif text-4xl font-semibold text-[#1A1A1A] mb-4">
              Meet the Team
            </h2>
            <p className="text-[#6B6B6B] max-w-lg mx-auto">
              Our leadership team brings decades of combined experience in
              Kenyan and international tax.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, i) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-[#FAF7F2] rounded-xl overflow-hidden hover:-translate-y-1 transition-all"
              >
                {/* Avatar area */}
                <div className="h-48 bg-[#6B1E2A]/8 relative flex items-center justify-center">
                  <div className="w-24 h-24 bg-[#6B1E2A] rounded-full flex items-center justify-center">
                    <span className="font-serif text-3xl font-bold text-white">
                      {member.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-[#1A1A1A] text-lg">
                    {member.name}
                  </h3>
                  <p className="text-sm text-[#6B1E2A] font-medium mb-3">
                    {member.role}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {member.qualifications.slice(0, 2).map((q) => (
                      <span
                        key={q}
                        className="px-2 py-0.5 bg-[#6B1E2A]/8 text-[#6B1E2A] text-[11px] font-medium rounded"
                      >
                        {q}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-[#6B6B6B] leading-relaxed line-clamp-3 mb-4">
                    {member.bio}
                  </p>
                  <div className="flex items-center gap-2">
                    <a
                      href={`mailto:${member.email}`}
                      className="w-8 h-8 bg-[#6B1E2A]/8 rounded-lg flex items-center justify-center hover:bg-[#6B1E2A] hover:text-white transition-colors group/icon"
                    >
                      <Mail
                        size={13}
                        className="text-[#6B1E2A] group-hover/icon:text-white"
                      />
                    </a>
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        className="w-8 h-8 bg-[#6B1E2A]/8 rounded-lg flex items-center justify-center hover:bg-[#6B1E2A] hover:text-white transition-colors group/icon"
                      >
                        <LinkedInIcon />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
